import { color, rgb, lab } from "d3";

// Color difference using CIE76 formula
// https://en.wikipedia.org/wiki/Color_difference#CIE76
export function colorDifference(color1, color2) {
    // convert to CIE Lab color space
    const c1 = lab(color1);
    const c2 = lab(color2);
    return Math.sqrt(Math.pow(c1.l - c2.l, 2) + Math.pow(c1.a - c2.a, 2) + Math.pow(c1.b - c2.b, 2));
}

//adopted from sass _mixColors function, its just a linear interpolation weighted by the alpha of each color
function mix(color1, color2, weight) {
    const weightScale = weight / 100;
    const normalizedWeight = weightScale * 2 - 1;
    const alphaDistance = color1.opacity - color2.opacity;

    const combinedWeight1 =
        normalizedWeight * alphaDistance == -1
            ? normalizedWeight
            : (normalizedWeight + alphaDistance) / (1 + normalizedWeight * alphaDistance);
    const weight1 = (combinedWeight1 + 1) / 2;
    const weight2 = 1 - weight1;

    return rgb(
        color1.r * weight1 + color2.r * weight2,
        color1.g * weight1 + color2.g * weight2,
        color1.b * weight1 + color2.b * weight2,
        color1.opacity * weightScale + color2.opacity * (1 - weightScale)
    );
}

//bootstrap tint and shade functions
function tintColor(c, weight) {
    return mix(color("white"), c, weight);
}

function shadeColor(c, weight) {
    return mix(color("black"), c, weight);
}

// Return WCAG2.1 relative luminance
// See https://www.w3.org/TR/WCAG/#dfn-relative-luminance
function luminance(color) {
    const RsRBG = color.r / 255;
    const GsRBG = color.g / 255;
    const BsRBG = color.b / 255;

    function sRGBtoRGB(value) {
        return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    }
    const R = sRGBtoRGB(RsRBG);
    const G = sRGBtoRGB(GsRBG);
    const B = sRGBtoRGB(BsRBG);

    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

// Return WCAG2.1 contrast ratio
// See https://www.w3.org/TR/WCAG/#dfn-contrast-ratio
function contrastRatio(color1, color2) {
    const L1 = luminance(color1);
    const L2 = luminance(color2);
    return (Math.max(L1, L2) + 0.05) / (Math.min(L1, L2) + 0.05);
}

// Returns the color that contrasts the most with the provided bgColorHex. By default, it will
// choose either black or white, but the caller may specify the foreground color options. This
// logic is identical what's in the the same-named function in _Site.cshtml
export function chooseHigherContrastColorAgainstBackground(
    bgColorHex,
    fstColorHex = "#ffffff",
    sndColorHex = "#000000"
) {
    const fstColor = color(fstColorHex);
    const sndColor = color(sndColorHex);
    const bgColor = color(bgColorHex);

    const contrastFst = contrastRatio(fstColor, bgColor);
    const contrastSnd = contrastRatio(sndColor, bgColor);

    return contrastFst > contrastSnd ? fstColor : sndColor;
}

// Get the value of a CSS property. These are stored at the body root for hosted content and in
// the child of the .pc-external element with the data-bs-theme attribute for versaform content.
export function getCssPropertyValue(propName) {
    const root =
        document.body.querySelector('.pc-external div[data-bs-theme]:not([data-bs-theme=""])') ?? document.body;
    const propValue = window.getComputedStyle(root).getPropertyValue(propName);

    return propValue;
}

// gets a foreground color that has a contrast ratio of at least 4.5:1 with the background color
// will just choose from black or white
function colorContrast(background) {
    const black = rgb(0, 0, 0);
    const white = rgb(255, 255, 255);
    if (contrastRatio(background, white) >= 4.5) {
        return white;
    }
    if (contrastRatio(background, black) >= 4.5) {
        return black;
    }
    console.warn(
        "No color with a contrast ratio of at least 4.5:1 found. Returning color with highest contrast ratio."
    );
    return contrastRatio(background, black) > contrastRatio(background, white) ? black : white;
}

function isWhite(color) {
    return color.r === 255 && color.g === 255 && color.b === 255;
}

// gets the button colors based on the background color. mirrors bootstrap 5's button color logic
export function getButtonColors(backgroundVal) {
    const background = color(backgroundVal);
    //bootstrap 5 button color logic constants. Technically these are bs5 variables, but we don't change them so we just use the default values
    const btn_hover_bg_shade_amount = 15;
    const btn_hover_bg_tint_amount = 15;
    const btn_hover_border_shade_amount = 20;
    const btn_hover_border_tint_amount = 10;
    const btn_active_bg_shade_amount = 20;
    const btn_active_bg_tint_amount = 20;
    const btn_active_border_shade_amount = 25;
    const btn_active_border_tint_amount = 10;

    const border = background;
    const colorObj = colorContrast(background);
    const hoverBackground = isWhite(colorObj)
        ? shadeColor(background, btn_hover_bg_shade_amount)
        : tintColor(background, btn_hover_bg_tint_amount);
    const hoverBorder = isWhite(colorObj)
        ? shadeColor(border, btn_hover_border_shade_amount)
        : tintColor(border, btn_hover_border_tint_amount);
    const hoverColor = colorContrast(hoverBackground);
    const activeBackground = isWhite(colorObj)
        ? shadeColor(background, btn_active_bg_shade_amount)
        : tintColor(background, btn_active_bg_tint_amount);
    const activeBorder = isWhite(colorObj)
        ? shadeColor(border, btn_active_border_shade_amount)
        : tintColor(border, btn_active_border_tint_amount);
    const activeColor = colorContrast(activeBackground);
    const disabledBackground = background;
    const disabledBorder = border;
    const disabledColor = colorContrast(disabledBackground);

    return {
        color: colorObj,
        background,
        border,
        hoverColor,
        hoverBackground,
        hoverBorder,

        focusShadow: mix(color, border, 15),

        activeColor,
        activeBackground,
        activeBorder,

        disabledColor,
        disabledBackground,
        disabledBorder,

        contrastRatio: contrastRatio(colorObj, background)
    };
}

export function getButtonColorsCssProperties(buttonColors) {
    return {
        "--bs-btn-color": buttonColors.color.formatHex(),
        "--bs-btn-bg": buttonColors.background.formatHex(),
        "--bs-btn-border-color": buttonColors.border.formatHex(),
        "--bs-btn-hover-color": buttonColors.hoverColor.formatHex(),
        "--bs-btn-hover-bg": buttonColors.hoverBackground.formatHex(),
        "--bs-btn-hover-border-color": buttonColors.hoverBorder.formatHex(),
        "--bs-btn-focus-shadow-rgb": buttonColors.focusShadow.formatRgb().slice(4, -1),
        "--bs-btn-active-color": buttonColors.activeColor.formatHex(),
        "--bs-btn-active-bg": buttonColors.activeBackground.formatHex(),
        "--bs-btn-active-border-color": buttonColors.activeBorder.formatHex(),
        "--bs-btn-disabled-color": buttonColors.disabledColor.formatHex(),
        "--bs-btn-disabled-bg": buttonColors.disabledBackground.formatHex(),
        "--bs-btn-disabled-border-color": buttonColors.disabledBorder.formatHex()
    };
}

export function getPrimaryColorPreviewCssProperties(primaryColor) {
    return {
        "--bs-primary": color(primaryColor).formatHex()
    };
}

export function getPcInputBoxShadowCssProperties(primaryColor) {
    const colorObj = color(primaryColor);
    colorObj.opacity = 0.25;
    // Default values from bootstrap 5 _variables.scss for $focus-ring-box-shadow for x-offset,
    // y-offset, blur, and width are: 0 0 0 0.25rem
    return `0 0 0 0.25rem rgba(${colorObj.r}, ${colorObj.g}, ${colorObj.b}, ${colorObj.opacity})`;
}

// Set the W/H to make the color picker square, mix the color picker selection with the box shadow
// for a softer look, and set the background to the same thing as the color picker selection so it
// looks like a single box instead of a box within a box
export function getColorPickerCssProperties(pickerColor) {
    return {
        boxShadow: `inset 0em 0.4em 0.4em -0.25em ${mix(color("black"), color(pickerColor), 50)}`,
        background: pickerColor,
        height: "3em",
        width: "3em"
    };
}

export function getRampForColor(colorVal) {
    // return the 050 - 900 color ramp for the given color
    const colorObj = color(colorVal);
    return {
        50: tintColor(colorObj, 90),
        100: tintColor(colorObj, 80),
        200: tintColor(colorObj, 60),
        300: tintColor(colorObj, 40),
        400: tintColor(colorObj, 20),
        500: colorObj,
        600: shadeColor(colorObj, 20),
        700: shadeColor(colorObj, 40),
        800: shadeColor(colorObj, 60),
        900: shadeColor(colorObj, 80)
    };
}

export function getRampCssPropertiesForColor(ramp, colorName) {
    // return the css properties for color ramp
    return {
        [`--bs-${colorName}-050-rgb`]: ramp[50].formatRgb().slice(4, -1),
        [`--bs-${colorName}-100-rgb`]: ramp[100].formatRgb().slice(4, -1),
        [`--bs-${colorName}-200-rgb`]: ramp[200].formatRgb().slice(4, -1),
        [`--bs-${colorName}-300-rgb`]: ramp[300].formatRgb().slice(4, -1),
        [`--bs-${colorName}-400-rgb`]: ramp[400].formatRgb().slice(4, -1),
        [`--bs-${colorName}-500-rgb`]: ramp[500].formatRgb().slice(4, -1),
        [`--bs-${colorName}-600-rgb`]: ramp[600].formatRgb().slice(4, -1),
        [`--bs-${colorName}-700-rgb`]: ramp[700].formatRgb().slice(4, -1),
        [`--bs-${colorName}-800-rgb`]: ramp[800].formatRgb().slice(4, -1),
        [`--bs-${colorName}-900-rgb`]: ramp[900].formatRgb().slice(4, -1),

        [`--bs-${colorName}-050`]: ramp[50].formatHex(),
        [`--bs-${colorName}-100`]: ramp[100].formatHex(),
        [`--bs-${colorName}-200`]: ramp[200].formatHex(),
        [`--bs-${colorName}-300`]: ramp[300].formatHex(),
        [`--bs-${colorName}-400`]: ramp[400].formatHex(),
        [`--bs-${colorName}-500`]: ramp[500].formatHex(),
        [`--bs-${colorName}-600`]: ramp[600].formatHex(),
        [`--bs-${colorName}-700`]: ramp[700].formatHex(),
        [`--bs-${colorName}-800`]: ramp[800].formatHex(),
        [`--bs-${colorName}-900`]: ramp[900].formatHex()
    };
}

export function getDerivedColorsForColor(colorVal) {
    const colorObj = color(colorVal);
    // return the text emphasis, bg subtle, and border subtle colors based on the given color
    // this is based on the bootstrap 5 definitions found in the _variables.scss file

    const textEmphasis = shadeColor(colorObj, 60);
    const bgSubtle = tintColor(colorObj, 80);
    const borderSubtle = tintColor(colorObj, 60);
    return {
        textEmphasis,
        bgSubtle,
        borderSubtle,
        contrastRatio: contrastRatio(textEmphasis, bgSubtle)
    };
}

export function getDerivedCssPropertiesForColor(derivedColors, colorStateName) {
    // return the css properties for text emphasis, bg subtle, and border subtle colors based on the given color
    return {
        [`--bs-${colorStateName}-text-emphasis`]: derivedColors.textEmphasis.formatHex(),
        [`--bs-${colorStateName}-bg-subtle`]: derivedColors.bgSubtle.formatHex(),
        [`--bs-${colorStateName}-border-subtle`]: derivedColors.borderSubtle.formatHex()
    };
}
